import { useState, useEffect, useCallback } from "react";
import { type LifecycleState, STATE_LABELS, STATE_COLORS } from "../plm/lifecycle";
import { SAMPLE_PARTS, type SamplePart } from "../samples/sampleParts";
import OrgManager from "../ui/OrgManager";
import { authClient } from "../auth/client";

interface Document {
  id: string;
  name: string;
  doc_type: string;
  part_number: string;
  revision: string;
  state: LifecycleState;
  org_id: string | null;
  org_name: string | null;
  owner_id: string;
  access_role: string;
  created_at: string;
  updated_at: string;
  thumbnail_url?: string;
}

interface DocumentListProps {
  onSelect: (doc: Document) => void;
  onLogout: () => void;
  userName: string;
}

export default function DocumentList({ onSelect, onLogout, userName }: DocumentListProps) {
  const [docs, setDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [creatingSampleId, setCreatingSampleId] = useState<string | null>(null);
  const [userOrgs, setUserOrgs] = useState<{ id: string; name: string }[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("personal");
  const [showOrgManager, setShowOrgManager] = useState(false);

  const fetchOrgs = useCallback(async () => {
    try {
      const { data } = await authClient.organization.list();
      if (data) {
        const orgs = Array.isArray(data) ? data : (data as any).data;
        if (Array.isArray(orgs)) {
          setUserOrgs(orgs.map(o => ({ id: o.id, name: o.name })));
        }
      }
    } catch { /* ignore */ }
  }, []);

  const fetchDocs = useCallback(async () => {
    try {
      const url = selectedOrgId ? `/api/documents?org_id=${selectedOrgId}` : "/api/documents";
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to load documents");
      const data = await res.json();
      setDocs(data);
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const handleCreate = async (docType: string = "part") => {
    setCreating(true);
    try {
      const res = await fetch("/api/documents", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Untitled",
          doc_type: docType,
          org_id: selectedOrgId === "personal" ? null : selectedOrgId
        }),
      });
      if (!res.ok) throw new Error("Failed to create document");
      const doc = await res.json();
      onSelect(doc);
    } catch (err: any) {
      setError(err.message || "Failed to create document");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm("Delete this document?")) return;
    try {
      const res = await fetch(`/api/documents/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete");
      setDocs((prev) => prev.filter((d) => d.id !== id));
    } catch (err: any) {
      setError(err.message || "Failed to delete document");
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
      " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };

  const myDocs = docs.filter((d) => d.access_role === "owner");
  const sharedDocs = docs.filter((d) => d.access_role !== "owner");

  const renderDocCard = (doc: Document, showDelete: boolean) => (
    <div key={doc.id} style={styles.card} onClick={() => onSelect(doc)}>
      <div style={styles.cardIcon}>
        {doc.thumbnail_url ? (
          <img src={doc.thumbnail_url} style={styles.cardThumbnail} alt="" />
        ) : doc.doc_type === "assembly" ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5">
            <rect x="3" y="6" width="8" height="12" rx="1"/>
            <rect x="13" y="4" width="8" height="12" rx="1"/>
            <path d="M11 12h2" strokeDasharray="2 1"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
        )}
      </div>
      <div style={styles.cardBody}>
        <div style={styles.cardNameRow}>
          <div style={styles.cardName}>{doc.name}</div>
          {doc.org_name && (
            <span style={styles.orgBadge}>{doc.org_name}</span>
          )}
          <span
            style={{
              ...styles.stateBadge,
              background: STATE_COLORS[doc.state]?.bg,
              color: STATE_COLORS[doc.state]?.fg,
            }}
          >
            {STATE_LABELS[doc.state] || doc.state}
          </span>
        </div>
        <div style={styles.cardMeta}>
          {doc.part_number && <span style={styles.partNumber}>{doc.part_number}</span>}
          {doc.access_role !== "owner" && (
            <span style={styles.roleBadge}>{doc.access_role}</span>
          )}
          <span style={styles.cardDate}>{formatDate(doc.updated_at)}</span>
        </div>
      </div>
      {showDelete && (
        <button
          onClick={(e) => handleDelete(e, doc.id)}
          style={styles.deleteBtn}
          title="Delete document"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 4h12M5.33 4V2.67a1.33 1.33 0 0 1 1.34-1.34h2.66a1.33 1.33 0 0 1 1.34 1.34V4M6.67 7.33v4M9.33 7.33v4M12.67 4v9.33a1.33 1.33 0 0 1-1.34 1.34H4.67a1.33 1.33 0 0 1-1.34-1.34V4"/>
          </svg>
        </button>
      )}
    </div>
  );

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.logo}>
              <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" width="14" height="14">
                <path d="M2 9L6 2L10 9H2Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
            </div>
            <span style={styles.brand}>CAD-PLM</span>
          </div>
          <div style={styles.headerRight}>
            <span style={styles.userName}>{userName}</span>
            <button
              onClick={() => setShowOrgManager(true)}
              style={styles.logoutBtn}
            >
              Teams
            </button>
            <button onClick={onLogout} style={styles.logoutBtn}>Sign out</button>
          </div>
        </div>

        {/* Custom Org Manager Modal */}
        {showOrgManager && (
          <div className="modal-overlay" onClick={() => { setShowOrgManager(false); fetchOrgs(); }}>
            <div className="modal-dialog" style={{ width: 640, maxWidth: "90vw", padding: 0 }} onClick={e => e.stopPropagation()}>
              <OrgManager />
              <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border-subtle)", textAlign: "right" }}>
                <button className="modal-btn" onClick={() => { setShowOrgManager(false); fetchOrgs(); }}>Close</button>
              </div>
            </div>
          </div>
        )}

        <div style={styles.titleRow}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <h2 style={styles.title}>My Documents</h2>
            {userOrgs.length > 0 && (
              <select
                aria-label="Workspace"
                style={styles.orgSelect}
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                <option value="personal">Personal Workspace</option>
                {userOrgs.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            )}
          </div>
          <button onClick={() => handleCreate("part")} disabled={creating} style={styles.newBtn}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M8 3v10M3 8h10"/>
            </svg>
            New Part
          </button>
          <button onClick={() => handleCreate("assembly")} disabled={creating} style={{ ...styles.newBtn, marginLeft: 6 }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="2" y="5" width="5" height="6" rx="0.5"/><rect x="9" y="3" width="5" height="6" rx="0.5"/><path d="M7 8h2" strokeDasharray="1 0.5"/>
            </svg>
            New Assembly
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <div style={styles.empty}>Loading documents...</div>
        ) : myDocs.length === 0 ? (
          <div style={styles.empty}>
            No documents yet. Create one to get started.
          </div>
        ) : (
          <div style={styles.grid}>
            {myDocs.map((doc) => renderDocCard(doc, true))}
          </div>
        )}

        {!loading && sharedDocs.length > 0 && (
          <>
            <h2 style={{ ...styles.title, marginTop: 32, marginBottom: 16 }}>Shared with me</h2>
            <div style={styles.grid}>
              {sharedDocs.map((doc) => renderDocCard(doc, false))}
            </div>
          </>
        )}

        {/* Sample Parts */}
        <h2 style={{ ...styles.title, marginTop: 32, marginBottom: 8 }}>
          Sample Parts
        </h2>
        <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>
          Pre-built examples with commented code — duplicate and modify to learn
        </p>
        <div style={styles.grid}>
          {SAMPLE_PARTS.map((sample) => (
            <div
              key={sample.id}
              style={{
                ...styles.card,
                opacity: creatingSampleId && creatingSampleId !== sample.id ? 0.5 : 1,
                cursor: creatingSampleId ? "default" : "pointer",
              }}
              onClick={async () => {
                if (creatingSampleId) return;
                setCreatingSampleId(sample.id);
                try {
                  const res = await fetch("/api/documents", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name: sample.name, doc_type: "part" }),
                  });
                  if (res.ok) {
                    const doc = await res.json();
                    // Store the sample script so App.tsx can load it
                    sessionStorage.setItem(`sample-script-${doc.id}`, sample.script);
                    onSelect(doc);
                  }
                } catch { /* ignore */ } finally {
                  setCreatingSampleId(null);
                }
              }}
            >
              <div style={styles.cardIcon}>
                {creatingSampleId === sample.id ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="1.5" style={{ animation: "spin 1s linear infinite" }}>
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="1.5">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                  </svg>
                )}
              </div>
              <div style={styles.cardBody}>
                <div style={styles.cardNameRow}>
                  <div style={styles.cardName}>{sample.name}</div>
                  <span style={{ ...styles.stateBadge, background: "rgba(61,220,132,0.15)", color: "var(--success)" }}>
                    {sample.category}
                  </span>
                </div>
                <div style={styles.cardMeta}>
                  <span style={styles.cardDate}>{sample.description}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    background: "var(--bg-void)",
    fontFamily: "var(--sans)",
    overflowY: "auto",
    paddingTop: 48,
    paddingBottom: 48,
  },
  container: {
    width: "100%",
    maxWidth: 680,
    padding: "0 24px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 32,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  logo: {
    width: 24,
    height: 24,
    borderRadius: 5,
    background: "linear-gradient(135deg, var(--accent) 0%, #3060c0 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  brand: {
    fontWeight: 700,
    fontSize: 14,
    color: "var(--text-bright)",
    letterSpacing: "-0.02em",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  userName: {
    fontSize: 12,
    color: "var(--text-secondary)",
  },
  logoutBtn: {
    background: "none",
    border: "1px solid var(--border-default)",
    color: "var(--text-secondary)",
    fontSize: 12,
    fontFamily: "var(--sans)",
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: "var(--text-bright)",
  },
  newBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    height: 34,
    padding: "0 14px",
    background: "var(--accent)",
    color: "#fff",
    border: "none",
    borderRadius: "var(--radius-md)",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "var(--sans)",
    cursor: "pointer",
  },
  error: {
    fontSize: 12,
    color: "var(--error)",
    marginBottom: 12,
    padding: "8px 12px",
    background: "rgba(240,80,80,0.1)",
    borderRadius: "var(--radius-sm)",
    border: "1px solid rgba(240,80,80,0.2)",
  },
  empty: {
    color: "var(--text-muted)",
    fontSize: 13,
    textAlign: "center" as const,
    padding: "48px 0",
  },
  grid: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
  },
  card: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    background: "var(--bg-base)",
    border: "1px solid var(--border-subtle)",
    borderRadius: "var(--radius-md)",
    cursor: "pointer",
    transition: "background 120ms ease, border-color 120ms ease",
  },
  cardIcon: {
    flexShrink: 0,
    opacity: 0.6,
  },
  cardThumbnail: {
    width: 48,
    height: 36,
    objectFit: "cover" as const,
    borderRadius: 4,
    display: "block",
    opacity: 1,
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  cardName: {
    fontSize: 13,
    fontWeight: 500,
    color: "var(--text-bright)",
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  stateBadge: {
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 600,
    padding: "2px 7px",
    borderRadius: 9999,
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    lineHeight: "16px",
  },
  orgBadge: {
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 600,
    padding: "2px 7px",
    borderRadius: 9999,
    background: "rgba(96,144,240,0.15)",
    color: "var(--accent)",
    letterSpacing: "0.02em",
    lineHeight: "16px",
  },
  roleBadge: {
    fontSize: 10,
    color: "var(--text-muted)",
    fontStyle: "italic" as const,
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 2,
  },
  partNumber: {
    fontSize: 11,
    color: "var(--text-secondary)",
    fontFamily: "var(--mono, monospace)",
  },
  cardDate: {
    fontSize: 11,
    color: "var(--text-muted)",
  },
  deleteBtn: {
    flexShrink: 0,
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    color: "var(--text-muted)",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
  },
  orgSelect: {
    height: 26,
    padding: "0 6px",
    background: "var(--bg-surface)",
    border: "1px solid var(--border-subtle)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-secondary)",
    fontSize: 11,
    outline: "none",
    cursor: "pointer",
  },
};
